import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  getAuth,
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  writeBatch,
  addDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { auth, db, isFirebaseConfigured, firebaseConfig } from '../firebase';
import {
  KNOWLEDGE_QUIZ_PASS_PERCENT,
  buildKnowledgeQuiz,
  normalizeStoredQuizQuestions,
  type KnowledgeQuizQuestion,
} from '../knowledgeQuiz';

export type UserRole = 'admin' | 'manager' | 'driver';
export type DriverStatus = 'Available' | 'On Route' | 'Off Duty';

export interface SDSUser {
  id: string;
  email: string;
  name: string;
  phone: string;
  role: UserRole;
  status: DriverStatus;
  active: boolean;
  lastLogin?: string;
  createdAt: string;
  knowledgeTrainingRequired?: boolean;
  knowledgeTopicsRead?: string[];
  knowledgeQuizPassed?: boolean;
  knowledgeQuizScore?: number;
  knowledgeQuizCompletedAt?: string;
  knowledgeQuizAttempts?: number;
}

export const driverNeedsKnowledgeTraining = (user: SDSUser | null | undefined) =>
  !!user &&
  user.role === 'driver' &&
  user.knowledgeTrainingRequired === true &&
  user.knowledgeQuizPassed !== true;

export interface LoginLog {
  id: string;
  userId: string;
  email: string;
  userName: string;
  role: UserRole;
  timestamp: string;
  status: 'Success' | 'Failed';
  userAgent?: string;
}

export interface ScheduleLog {
  id: string;
  adminId: string;
  adminName: string;
  adminEmail: string;
  driverId: string;
  driverName: string;
  day: string;
  windowLabel: string;
  action: 'Assigned' | 'Removed' | 'Marked Off' | 'Restored Active' | 'Bulk Assigned' | 'Updated';
  details: string;
  timestamp: string;
}

export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

export const DAY_OF_WEEK_FROM_JS_INDEX: DayOfWeek[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const toDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const getDayOfWeekName = (date: Date): DayOfWeek => {
  return DAY_OF_WEEK_FROM_JS_INDEX[date.getDay()];
};

export const formatCalendarDateLabel = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatShortCalendarDateLabel = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export type DeliveryWindowKey = 'morning' | 'afternoon' | 'evening';

export const DELIVERY_WINDOWS: {
  key: DeliveryWindowKey;
  label: string;
  timeRange: string;
  fullLabel: string;
}[] = [
  {
    key: 'morning',
    label: 'Morning',
    timeRange: '8:00am - 12:00pm',
    fullLabel: 'Morning (8:00am - 12:00pm)',
  },
  {
    key: 'afternoon',
    label: 'Afternoon',
    timeRange: '1:00pm - 7:00pm',
    fullLabel: 'Afternoon (1:00pm - 7:00pm)',
  },
  {
    key: 'evening',
    label: 'Evening',
    timeRange: '7:00pm - 12:00am',
    fullLabel: 'Evening (7:00pm - 12:00am)',
  },
];

export const parseShiftWindows = (shiftStr?: string): DeliveryWindowKey[] => {
  if (!shiftStr || shiftStr.toLowerCase() === 'off') return [];
  const lower = shiftStr.toLowerCase();
  const keys: DeliveryWindowKey[] = [];
  if (lower.includes('morning') || lower.includes('8:00am - 12:00pm')) keys.push('morning');
  if (lower.includes('afternoon') || lower.includes('1:00pm - 7:00pm')) keys.push('afternoon');
  if (lower.includes('evening') || lower.includes('7:00pm - 12:00am')) keys.push('evening');
  return keys;
};

export const formatShiftWindows = (keys: DeliveryWindowKey[]): string => {
  if (keys.length === 0) return 'Off';
  const order: DeliveryWindowKey[] = ['morning', 'afternoon', 'evening'];
  const sorted = order.filter((k) => keys.includes(k));
  return sorted
    .map((k) => DELIVERY_WINDOWS.find((w) => w.key === k)?.fullLabel || k)
    .join(', ');
};

export interface WeeklySchedule {
  driverId: string;
  driverName: string;
  shifts: Record<DayOfWeek, string>;
  windows?: Record<DayOfWeek, DeliveryWindowKey[]>;
  dateShifts?: Record<string, string>;
  markedOff?: Record<string, boolean>;
  updatedAt: string;
}

export const getScheduleShiftForDate = (
  sched: WeeklySchedule | undefined,
  date: Date
): string => {
  if (!sched) return 'Off';
  const dateKey = toDateKey(date);
  if (sched.dateShifts && sched.dateShifts[dateKey] !== undefined) {
    return sched.dateShifts[dateKey];
  }
  const dayName = getDayOfWeekName(date);
  return sched.shifts?.[dayName] || 'Off';
};

export const isScheduleShiftMarkedOff = (
  sched: WeeklySchedule | undefined,
  date: Date,
  windowKey: DeliveryWindowKey
): boolean => {
  if (!sched?.markedOff) return false;
  const dateKey = toDateKey(date);
  const dateShiftKey = `${dateKey}-${windowKey}`;
  if (sched.markedOff[dateShiftKey] !== undefined) {
    return Boolean(sched.markedOff[dateShiftKey]);
  }
  const dayName = getDayOfWeekName(date);
  return Boolean(sched.markedOff[`${dayName}-${windowKey}`]);
};

export type KBCategory = string;

export interface KnowledgeBaseSection {
  key: string;
  title: string;
  subtitle: string;
  color: string;
}

export const DEFAULT_KB_SECTIONS: KnowledgeBaseSection[] = [
  {
    key: 'Delivery & Routing Procedures',
    title: 'Section 1: Delivery & Routing Procedures',
    subtitle:
      'Pre-route checks, specimen chain-of-custody, route sequence, delays, and STAT delivery protocols.',
    color: '#1565C0',
  },
  {
    key: 'EC-Mobile App Usage',
    title: 'Section 2: EC-Mobile App Usage',
    subtitle:
      'Logging in, loading manifests, barcode scanning at pickup/drop-off, and capturing Proof of Delivery (POD).',
    color: '#2E7D32',
  },
  {
    key: 'General & Safety',
    title: 'General & Safety Guidelines',
    subtitle: 'Additional SDS courier policies, safety procedures, and reference guides.',
    color: '#6A1B9A',
  },
];

const KB_SECTIONS_DOC_ID = 'kb-sections';
const QUIZ_BANK_DOC_ID = 'quiz-bank';

const normalizeKnowledgeSections = (raw: unknown): KnowledgeBaseSection[] | null => {
  if (!Array.isArray(raw)) return null;
  const sections = raw
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Partial<KnowledgeBaseSection>;
      const key = typeof record.key === 'string' ? record.key.trim() : '';
      const title = typeof record.title === 'string' ? record.title.trim() : '';
      if (!key || !title) return null;
      return {
        key,
        title,
        subtitle: typeof record.subtitle === 'string' ? record.subtitle : '',
        color:
          typeof record.color === 'string' && record.color
            ? record.color
            : DEFAULT_KB_SECTIONS[index % DEFAULT_KB_SECTIONS.length].color,
      };
    })
    .filter((item): item is KnowledgeBaseSection => Boolean(item));
  return sections.length ? sections : null;
};

export interface KnowledgeBaseArticle {
  id: string;
  category: KBCategory;
  title: string;
  summary: string;
  steps: string[];
  notes?: string;
  order?: number;
  updatedAt: string;
  updatedBy?: string;
}

export const topicsInCategory = (
  articles: KnowledgeBaseArticle[],
  category: KBCategory
): KnowledgeBaseArticle[] => {
  return articles
    .map((article, index) => ({ article, index }))
    .filter((entry) => entry.article.category === category)
    .sort((a, b) => {
      const aOrder = a.article.order ?? a.index;
      const bOrder = b.article.order ?? b.index;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.index - b.index;
    })
    .map((entry) => entry.article);
};

export interface TrainingScore {
  id: string;
  driverId: string;
  driverName: string;
  driverEmail: string;
  moduleName: string;
  score: number;
  maxScore: number;
  passed: boolean;
  completedAt: string;
}

interface AuthContextType {
  currentUser: SDSUser | null;
  loading: boolean;
  isFirebaseLive: boolean;
  users: SDSUser[];
  schedules: WeeklySchedule[];
  trainingScores: TrainingScore[];
  loginLogs: LoginLog[];
  scheduleLogs: ScheduleLog[];
  knowledgeBase: KnowledgeBaseArticle[];
  kbSections: KnowledgeBaseSection[];
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; user?: SDSUser }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  addDriverAccount: (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    role: UserRole;
    status: DriverStatus;
  }) => Promise<{ success: boolean; error?: string }>;
  removeDriverAccount: (userId: string) => Promise<void>;
  updateDriverStatus: (userId: string, status: DriverStatus) => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  updateUserDetails: (
    userId: string,
    updates: { name: string; email: string; phone?: string; role?: UserRole }
  ) => Promise<{ success: boolean; error?: string }>;
  requireKnowledgeQuizRetake: (userId: string) => Promise<{ success: boolean; error?: string }>;
  updateDriverSchedule: (
    driverId: string,
    driverName: string,
    shifts: Record<DayOfWeek, string>,
    markedOff?: Record<string, boolean>,
    logMeta?: {
      day: string;
      windowLabel: string;
      action: ScheduleLog['action'];
      details: string;
    },
    dateShifts?: Record<string, string>
  ) => Promise<{ success: boolean; error?: string }>;
  clearAllDriverSchedules: () => Promise<{ success: boolean; error?: string }>;
  seedAdminAccount: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  saveKnowledgeArticle: (
    article: Omit<KnowledgeBaseArticle, 'id' | 'updatedAt' | 'updatedBy'> & { id?: string }
  ) => Promise<{ success: boolean; error?: string }>;
  deleteKnowledgeArticle: (articleId: string) => Promise<{ success: boolean; error?: string }>;
  reorderKnowledgeTopics: (
    category: KBCategory,
    orderedIds: string[]
  ) => Promise<{ success: boolean; error?: string }>;
  updateKnowledgeSection: (
    previousKey: string,
    updates: { key: string; title: string; subtitle: string }
  ) => Promise<{ success: boolean; error?: string }>;
  markKnowledgeTopicRead: (articleId: string) => Promise<{ success: boolean; error?: string }>;
  quizQuestions: KnowledgeQuizQuestion[];
  quizBankReady: boolean;
  saveQuizQuestion: (
    question: KnowledgeQuizQuestion
  ) => Promise<{ success: boolean; error?: string }>;
  deleteQuizQuestion: (questionId: string) => Promise<{ success: boolean; error?: string }>;
  publishGeneratedQuiz: () => Promise<{ success: boolean; error?: string; count?: number }>;
  submitKnowledgeQuiz: (
    correctCount: number,
    questionCount: number
  ) => Promise<{ success: boolean; error?: string; passed?: boolean; score?: number }>;
  quizInProgress: boolean;
  setKnowledgeQuizActive: (active: boolean) => void;
  refreshData: () => Promise<void>;
}

const DEFAULT_DAYS: Record<DayOfWeek, string> = {
  Monday: 'Morning (8:00am - 12:00pm), Afternoon (1:00pm - 7:00pm)',
  Tuesday: 'Morning (8:00am - 12:00pm), Afternoon (1:00pm - 7:00pm)',
  Wednesday: 'Morning (8:00am - 12:00pm), Afternoon (1:00pm - 7:00pm)',
  Thursday: 'Morning (8:00am - 12:00pm), Afternoon (1:00pm - 7:00pm)',
  Friday: 'Morning (8:00am - 12:00pm), Afternoon (1:00pm - 7:00pm)',
  Saturday: 'Off',
  Sunday: 'Off',
};

const INITIAL_DEMO_USERS: SDSUser[] = [
  {
    id: 'admin-1',
    email: 'admin@sds.com',
    name: 'SDS Administrator',
    phone: '(602) 555-0100',
    role: 'admin',
    status: 'Available',
    active: true,
    lastLogin: new Date().toISOString(),
    createdAt: new Date(Date.now() - 86400000 * 30).toISOString(),
  },
  {
    id: 'driver-1',
    email: 'driver1@sds.com',
    name: 'Marcus Vance',
    phone: '(602) 555-0142',
    role: 'driver',
    status: 'Available',
    active: true,
    lastLogin: new Date(Date.now() - 3600000 * 2).toISOString(),
    createdAt: new Date(Date.now() - 86400000 * 14).toISOString(),
  },
  {
    id: 'driver-2',
    email: 'driver2@sds.com',
    name: 'Elena Rostova',
    phone: '(480) 555-0198',
    role: 'driver',
    status: 'On Route',
    active: true,
    lastLogin: new Date(Date.now() - 3600000 * 5).toISOString(),
    createdAt: new Date(Date.now() - 86400000 * 10).toISOString(),
  },
  {
    id: 'driver-3',
    email: 'driver3@sds.com',
    name: 'Devon Brooks',
    phone: '(623) 555-0174',
    role: 'driver',
    status: 'Off Duty',
    active: true,
    lastLogin: new Date(Date.now() - 86400000).toISOString(),
    createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
  },
];

const INITIAL_DEMO_SCHEDULES: WeeklySchedule[] = [
  {
    driverId: 'driver-1',
    driverName: 'Marcus Vance',
    shifts: { ...DEFAULT_DAYS },
    updatedAt: new Date().toISOString(),
  },
  {
    driverId: 'driver-2',
    driverName: 'Elena Rostova',
    shifts: {
      Monday: 'Off',
      Tuesday: 'Afternoon (1:00pm - 7:00pm)',
      Wednesday: 'Afternoon (1:00pm - 7:00pm)',
      Thursday: 'Afternoon (1:00pm - 7:00pm)',
      Friday: 'Afternoon (1:00pm - 7:00pm)',
      Saturday: 'Morning (8:00am - 12:00pm)',
      Sunday: 'Off',
    },
    updatedAt: new Date().toISOString(),
  },
  {
    driverId: 'driver-3',
    driverName: 'Devon Brooks',
    shifts: {
      Monday: 'Evening (7:00pm - 12:00am)',
      Tuesday: 'Evening (7:00pm - 12:00am)',
      Wednesday: 'Off',
      Thursday: 'Off',
      Friday: 'Evening (7:00pm - 12:00am)',
      Saturday: 'Evening (7:00pm - 12:00am)',
      Sunday: 'Evening (7:00pm - 12:00am)',
    },
    updatedAt: new Date().toISOString(),
  },
];

const INITIAL_KNOWLEDGE_BASE: KnowledgeBaseArticle[] = [
  {
    id: 'kb-routing-1',
    order: 0,
    category: 'Delivery & Routing Procedures',
    title: 'Pre-Route Vehicle & Equipment Check',
    summary:
      'Required steps before starting your assigned Morning, Afternoon, or Evening delivery shift.',
    steps: [
      'Inspect vehicle fuel/charge level, tire condition, and clean cargo area before departure.',
      'Verify you have all required SDS courier supplies: insulated coolers (ambient, refrigerated, frozen), ice packs, spill kit, and SDS ID badge.',
      'Ensure your mobile device is charged and you have a vehicle charger ready for route navigation and EC-Mobile scanning.',
      'Review your assigned delivery window and stop sequence before leaving the facility.',
    ],
    notes:
      'Always wear your SDS identification badge visibly when entering hospitals, clinics, or labs.',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'kb-routing-2',
    order: 1,
    category: 'Delivery & Routing Procedures',
    title: 'Pickup, Chain-of-Custody & Specimen Handling',
    summary:
      'Standard operating procedure for picking up medical specimens, pharmaceuticals, and supplies at client locations.',
    steps: [
      'Arrive within the scheduled pickup window and check in at the designated department or lockbox.',
      'Verify specimen/package count and check that all biohazard bags are properly sealed and labeled.',
      'Place specimens immediately into the appropriate temperature-controlled tote (Ambient, Refrigerated 2°C–8°C, or Frozen).',
      'Scan every package barcode at the point of pickup before departing the stop.',
    ],
    notes:
      'Never leave medical specimens or pharmaceuticals unattended in an unlocked vehicle.',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'kb-routing-3',
    order: 2,
    category: 'Delivery & Routing Procedures',
    title: 'Route Optimization, Delays & STAT Deliveries',
    summary:
      'How to follow stop sequences, handle traffic or facility delays, and prioritize urgent STAT orders.',
    steps: [
      'Follow the assigned stop sequence unless instructed otherwise by SDS Dispatch/Admin.',
      'If a STAT or Urgent order is dispatched to you mid-route, acknowledge immediately and prioritize the STAT pickup/delivery.',
      'If you encounter a delay of 15+ minutes (traffic, weather, or waiting at a clinic), notify SDS Dispatch right away.',
      'At delivery drop-off, hand-deliver to authorized personnel and capture the recipient name and signature.',
    ],
    notes:
      'For lockbox drop-offs after hours, ensure the lockbox is securely latched after placing the order inside.',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'kb-ecmobile-1',
    order: 0,
    category: 'EC-Mobile App Usage',
    title: 'Logging In & Starting Your Shift in EC-Mobile',
    summary:
      'How to sign in, enable location services, and load your manifest in the EC-Mobile driver app.',
    steps: [
      'Open the EC-Mobile app on your smartphone and sign in with your assigned driver credentials.',
      'Ensure Location Services (GPS) and Camera permissions are set to "Allow" so route tracking and barcode scanning work properly.',
      'Tap "Manifest" or "My Stops" on the home screen to view all assigned pickups and deliveries for your current shift.',
      'Review stop notes, suite numbers, and special entry codes before starting navigation.',
    ],
    notes:
      'If your manifest does not appear, pull down to refresh or tap Sync in the EC-Mobile menu.',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'kb-ecmobile-2',
    order: 1,
    category: 'EC-Mobile App Usage',
    title: 'Scanning Barcodes at Pickup & Delivery',
    summary:
      'Step-by-step instructions for scanning packages and confirming piece counts in EC-Mobile.',
    steps: [
      'Upon arriving at a stop, tap the stop in EC-Mobile and select "Arrive / Pickup" or "Arrive / Deliver".',
      'Tap the barcode scanner icon and scan each package or specimen bag label until all items show as verified.',
      'If a barcode is damaged or won’t scan, use the manual entry option to type the tracking/manifest number.',
      'Confirm the total piece count matches the manifest before proceeding to the next screen.',
    ],
    notes:
      'Always scan items at the actual pickup/drop-off location—never pre-scan in the vehicle.',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'kb-ecmobile-3',
    order: 2,
    category: 'EC-Mobile App Usage',
    title: 'Capturing Proof of Delivery (POD), Signatures & Photos',
    summary:
      'Completing a stop in EC-Mobile with recipient signature, printed name, or photo proof of delivery.',
    steps: [
      'After scanning all items for delivery, tap "Complete Stop" / "POD" in EC-Mobile.',
      'Type the receiving staff member’s full first and last name clearly in the "Received By" field.',
      'Have the recipient sign directly on the screen and tap Save/Submit.',
      'For authorized unattended/lockbox deliveries, take a clear photo showing the package inside the secure location and add a brief POD note.',
    ],
    notes:
      'Double-check that the stop status changes to "Completed" before driving to your next stop.',
    updatedAt: new Date().toISOString(),
  },
];

const INITIAL_DEMO_SCORES: TrainingScore[] = [
  {
    id: 'score-1',
    driverId: 'driver-1',
    driverName: 'Marcus Vance',
    driverEmail: 'driver1@sds.com',
    moduleName: 'HIPAA & Patient Privacy Compliance',
    score: 96,
    maxScore: 100,
    passed: true,
    completedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
  },
  {
    id: 'score-2',
    driverId: 'driver-1',
    driverName: 'Marcus Vance',
    driverEmail: 'driver1@sds.com',
    moduleName: 'Biohazard & Specimen Handling (Cold Chain)',
    score: 92,
    maxScore: 100,
    passed: true,
    completedAt: new Date(Date.now() - 86400000 * 4).toISOString(),
  },
  {
    id: 'score-3',
    driverId: 'driver-2',
    driverName: 'Elena Rostova',
    driverEmail: 'driver2@sds.com',
    moduleName: 'HIPAA & Patient Privacy Compliance',
    score: 100,
    maxScore: 100,
    passed: true,
    completedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
  },
  {
    id: 'score-4',
    driverId: 'driver-3',
    driverName: 'Devon Brooks',
    driverEmail: 'driver3@sds.com',
    moduleName: 'Emergency STAT Delivery Protocols',
    score: 74,
    maxScore: 100,
    passed: false,
    completedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEYS = {
  CURRENT_USER: 'sds_current_user',
  USERS: 'sds_users',
  PASSWORDS: 'sds_passwords',
  SCHEDULES: 'sds_schedules',
  SCORES: 'sds_training_scores',
  LOGS: 'sds_login_logs',
  SCHEDULE_LOGS: 'sds_schedule_logs',
  KNOWLEDGE_BASE: 'sds_knowledge_base',
  KB_SECTIONS: 'sds_kb_sections',
  QUIZ_BANK: 'sds_quiz_bank',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<SDSUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<SDSUser[]>([]);
  const [schedules, setSchedules] = useState<WeeklySchedule[]>([]);
  const [trainingScores, setTrainingScores] = useState<TrainingScore[]>([]);
  const [loginLogs, setLoginLogs] = useState<LoginLog[]>([]);
  const [scheduleLogs, setScheduleLogs] = useState<ScheduleLog[]>([]);
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBaseArticle[]>(
    INITIAL_KNOWLEDGE_BASE
  );
  const [kbSections, setKbSections] = useState<KnowledgeBaseSection[]>(DEFAULT_KB_SECTIONS);
  const [quizQuestions, setQuizQuestions] = useState<KnowledgeQuizQuestion[]>([]);
  const [quizBankReady, setQuizBankReady] = useState(false);
  const [quizInProgress, setQuizInProgress] = useState(false);
  const setKnowledgeQuizActive = useCallback((active: boolean) => {
    setQuizInProgress(active);
  }, []);

  // Load local fallback data or Firestore data
  const loadLocalData = () => {
    const savedUsers = localStorage.getItem(STORAGE_KEYS.USERS);
    const parsedUsers: SDSUser[] = savedUsers ? JSON.parse(savedUsers) : INITIAL_DEMO_USERS;
    if (!savedUsers) {
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(INITIAL_DEMO_USERS));
      localStorage.setItem(
        STORAGE_KEYS.PASSWORDS,
        JSON.stringify({
          'admin@sds.com': 'admin123',
          'driver1@sds.com': 'driver123',
          'driver2@sds.com': 'driver123',
          'driver3@sds.com': 'driver123',
        })
      );
    }

    const savedSchedules = localStorage.getItem(STORAGE_KEYS.SCHEDULES);
    const parsedSchedules: WeeklySchedule[] = savedSchedules
      ? JSON.parse(savedSchedules)
      : INITIAL_DEMO_SCHEDULES;
    if (!savedSchedules) {
      localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(INITIAL_DEMO_SCHEDULES));
    }

    const savedScores = localStorage.getItem(STORAGE_KEYS.SCORES);
    const parsedScores: TrainingScore[] = savedScores
      ? JSON.parse(savedScores)
      : INITIAL_DEMO_SCORES;
    if (!savedScores) {
      localStorage.setItem(STORAGE_KEYS.SCORES, JSON.stringify(INITIAL_DEMO_SCORES));
    }

    const savedLogs = localStorage.getItem(STORAGE_KEYS.LOGS);
    const parsedLogs: LoginLog[] = savedLogs ? JSON.parse(savedLogs) : [];

    const savedSchedLogs = localStorage.getItem(STORAGE_KEYS.SCHEDULE_LOGS);
    const parsedSchedLogs: ScheduleLog[] = savedSchedLogs ? JSON.parse(savedSchedLogs) : [];

    const savedKB = localStorage.getItem(STORAGE_KEYS.KNOWLEDGE_BASE);
    const parsedKB: KnowledgeBaseArticle[] = savedKB
      ? JSON.parse(savedKB)
      : INITIAL_KNOWLEDGE_BASE;
    if (!savedKB) {
      localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_BASE, JSON.stringify(INITIAL_KNOWLEDGE_BASE));
    }

    setUsers(parsedUsers);
    setSchedules(parsedSchedules);
    setTrainingScores(parsedScores);
    setLoginLogs(parsedLogs);
    setScheduleLogs(parsedSchedLogs);
    setKnowledgeBase(parsedKB);
    const savedQuiz = localStorage.getItem(STORAGE_KEYS.QUIZ_BANK);
    if (savedQuiz) {
      setQuizBankReady(true);
      setQuizQuestions(normalizeStoredQuizQuestions(JSON.parse(savedQuiz)));
    } else {
      setQuizBankReady(false);
      setQuizQuestions([]);
    }
    const savedSections = localStorage.getItem(STORAGE_KEYS.KB_SECTIONS);
    setKbSections(
      savedSections ? normalizeKnowledgeSections(JSON.parse(savedSections)) || DEFAULT_KB_SECTIONS : DEFAULT_KB_SECTIONS
    );
  };

  const fetchFirestoreData = async (activeUser?: SDSUser | null) => {
    if (!db) return;
    const role = activeUser?.role || currentUser?.role;
    const uid = activeUser?.id || currentUser?.id;

    try {
      if (role === 'driver' && uid) {
        // Drivers only load their own schedule
        const mySchedSnap = await getDoc(doc(db, 'schedules', uid));
        if (mySchedSnap.exists()) {
          const data = mySchedSnap.data() as WeeklySchedule;
          setSchedules([{ ...data, driverId: data.driverId || mySchedSnap.id }]);
        } else {
          setSchedules([]);
        }
      } else {
        // Admins and Managers load all users and everyone's schedules
        const usersSnap = await getDocs(collection(db, 'users'));
        const loadedUsers: SDSUser[] = [];
        usersSnap.forEach((docSnap) => {
          loadedUsers.push({ id: docSnap.id, ...(docSnap.data() as Omit<SDSUser, 'id'>) });
        });
        setUsers(loadedUsers);

        const schedSnap = await getDocs(collection(db, 'schedules'));
        const loadedSchedules: WeeklySchedule[] = [];
        schedSnap.forEach((docSnap) => {
          const data = docSnap.data() as WeeklySchedule;
          loadedSchedules.push({
            ...data,
            driverId: data.driverId || docSnap.id,
          });
        });
        setSchedules(loadedSchedules);
      }

      const scoresSnap = await getDocs(collection(db, 'trainingScores'));
      const loadedScores: TrainingScore[] = [];
      scoresSnap.forEach((docSnap) => {
        loadedScores.push({ id: docSnap.id, ...(docSnap.data() as Omit<TrainingScore, 'id'>) });
      });
      setTrainingScores(loadedScores);

      // Load or seed Knowledge Base articles in Firebase
      const kbSnap = await getDocs(collection(db, 'knowledgeBase'));
      if (kbSnap.empty) {
        for (const item of INITIAL_KNOWLEDGE_BASE) {
          await setDoc(doc(db, 'knowledgeBase', item.id), item).catch(() => {});
        }
        setKnowledgeBase(INITIAL_KNOWLEDGE_BASE);
        setQuizBankReady(false);
        setQuizQuestions([]);
      } else {
        const loadedKB: KnowledgeBaseArticle[] = [];
        let loadedSections = DEFAULT_KB_SECTIONS;
        let loadedQuiz: KnowledgeQuizQuestion[] = [];
        let quizReady = false;
        kbSnap.forEach((docSnap) => {
          if (docSnap.id === QUIZ_BANK_DOC_ID) {
            quizReady = true;
            loadedQuiz = normalizeStoredQuizQuestions(docSnap.data().questions);
            return;
          }
          if (docSnap.id === KB_SECTIONS_DOC_ID) {
            loadedSections =
              normalizeKnowledgeSections(docSnap.data().sections) || DEFAULT_KB_SECTIONS;
            return;
          }
          const data = docSnap.data() as Partial<KnowledgeBaseArticle>;
          if (!data.title || !data.category) return;
          loadedKB.push({
            id: docSnap.id,
            ...(data as Omit<KnowledgeBaseArticle, 'id'>),
          });
        });
        setKbSections(loadedSections);
        setKnowledgeBase(loadedKB);
        setQuizBankReady(quizReady);
        setQuizQuestions(loadedQuiz);
      }

      if (role === 'admin' || role === 'manager') {
        const logsQuery = query(collection(db, 'loginLogs'), orderBy('timestamp', 'desc'), limit(50));
        const logsSnap = await getDocs(logsQuery);
        const loadedLogs: LoginLog[] = [];
        logsSnap.forEach((docSnap) => {
          loadedLogs.push({ id: docSnap.id, ...(docSnap.data() as Omit<LoginLog, 'id'>) });
        });
        setLoginLogs(loadedLogs);
      }

      if (role === 'admin') {
        const schedLogsQuery = query(
          collection(db, 'scheduleLogs'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const schedLogsSnap = await getDocs(schedLogsQuery);
        const loadedSchedLogs: ScheduleLog[] = [];
        schedLogsSnap.forEach((docSnap) => {
          loadedSchedLogs.push({
            id: docSnap.id,
            ...(docSnap.data() as Omit<ScheduleLog, 'id'>),
          });
        });
        setScheduleLogs(loadedSchedLogs);
      } else {
        setScheduleLogs([]);
      }
    } catch (err) {
      console.error('Error fetching Firestore data:', err);
    }
  };

  useEffect(() => {
    if (isFirebaseConfigured && auth && db) {
      let unsubSchedules: (() => void) | null = null;
      let unsubUsers: (() => void) | null = null;
      let unsubScheduleLogs: (() => void) | null = null;
      let unsubProfile: (() => void) | null = null;

      const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        if (unsubSchedules) {
          unsubSchedules();
          unsubSchedules = null;
        }
        if (unsubUsers) {
          unsubUsers();
          unsubUsers = null;
        }
        if (unsubScheduleLogs) {
          unsubScheduleLogs();
          unsubScheduleLogs = null;
        }
        if (unsubProfile) {
          unsubProfile();
          unsubProfile = null;
        }

        if (fbUser && db) {
          try {
            let activeProfile: SDSUser;
            const userDoc = await getDoc(doc(db, 'users', fbUser.uid));
            if (userDoc.exists()) {
              activeProfile = { id: fbUser.uid, ...(userDoc.data() as Omit<SDSUser, 'id'>) };
              setCurrentUser(activeProfile);
            } else {
              // Auto-provision profile if not found in Firestore yet
              const defaultRole: UserRole = fbUser.email?.toLowerCase().includes('admin')
                ? 'admin'
                : 'driver';
              activeProfile = {
                id: fbUser.uid,
                email: fbUser.email || '',
                name: fbUser.displayName || fbUser.email?.split('@')[0] || 'SDS User',
                phone: '',
                role: defaultRole,
                status: 'Available',
                active: true,
                lastLogin: new Date().toISOString(),
                createdAt: new Date().toISOString(),
              };
              await setDoc(doc(db, 'users', fbUser.uid), activeProfile);
              setCurrentUser(activeProfile);
            }

            if (activeProfile.role === 'driver') {
              unsubProfile = onSnapshot(doc(db, 'users', fbUser.uid), (profileSnap) => {
                if (!profileSnap.exists()) return;
                setCurrentUser({
                  id: profileSnap.id,
                  ...(profileSnap.data() as Omit<SDSUser, 'id'>),
                });
              });
              // Drivers only listen to their own schedule document
              unsubSchedules = onSnapshot(
                doc(db, 'schedules', fbUser.uid),
                (docSnap) => {
                  if (docSnap.exists()) {
                    const data = docSnap.data() as WeeklySchedule;
                    setSchedules([{ ...data, driverId: data.driverId || docSnap.id }]);
                  } else {
                    setSchedules([]);
                  }
                },
                (err) => {
                  console.error('Error listening to personal schedule:', err);
                }
              );
            } else {
              // Admins and Managers listen to everyone's schedules and users
              unsubSchedules = onSnapshot(
                collection(db, 'schedules'),
                (snap) => {
                  const liveSchedules: WeeklySchedule[] = [];
                  snap.forEach((docSnap) => {
                    const data = docSnap.data() as WeeklySchedule;
                    liveSchedules.push({
                      ...data,
                      driverId: data.driverId || docSnap.id,
                    });
                  });
                  setSchedules(liveSchedules);
                },
                (err) => {
                  console.error('Error listening to schedules collection:', err);
                }
              );

              unsubUsers = onSnapshot(
                collection(db, 'users'),
                (snap) => {
                  const liveUsers: SDSUser[] = [];
                  snap.forEach((docSnap) => {
                    liveUsers.push({ id: docSnap.id, ...(docSnap.data() as Omit<SDSUser, 'id'>) });
                  });
                  setUsers(liveUsers);
                },
                (err) => {
                  console.error('Error listening to users collection:', err);
                }
              );

              if (activeProfile.role === 'admin') {
                const schedLogsQuery = query(
                  collection(db, 'scheduleLogs'),
                  orderBy('timestamp', 'desc'),
                  limit(100)
                );
                unsubScheduleLogs = onSnapshot(
                  schedLogsQuery,
                  (snap) => {
                    const liveSchedLogs: ScheduleLog[] = [];
                    snap.forEach((docSnap) => {
                      liveSchedLogs.push({
                        id: docSnap.id,
                        ...(docSnap.data() as Omit<ScheduleLog, 'id'>),
                      });
                    });
                    setScheduleLogs(liveSchedLogs);
                  },
                  (err) => {
                    console.error('Error listening to scheduleLogs collection:', err);
                  }
                );
              }
            }

            await fetchFirestoreData(activeProfile);
          } catch (e) {
            console.error('Error loading user profile from Firestore:', e);
          }
        } else {
          setCurrentUser(null);
        }
        setLoading(false);
      });
      return () => {
        unsubscribe();
        if (unsubSchedules) unsubSchedules();
        if (unsubUsers) unsubUsers();
        if (unsubScheduleLogs) unsubScheduleLogs();
        if (unsubProfile) unsubProfile();
      };
    } else {
      loadLocalData();
      const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
      if (savedUser) {
        setCurrentUser(JSON.parse(savedUser));
      }
      setLoading(false);
    }
  }, []);

  const recordLoginEvent = async (
    user: SDSUser | null,
    emailAttempt: string,
    status: 'Success' | 'Failed'
  ) => {
    const timestamp = new Date().toISOString();
    const newLog: LoginLog = {
      id: `log-${Date.now()}`,
      userId: user?.id || 'unknown',
      email: emailAttempt,
      userName: user?.name || 'Unknown User',
      role: user?.role || 'driver',
      timestamp,
      status,
      userAgent: navigator.userAgent,
    };

    if (isFirebaseConfigured && db) {
      try {
        await addDoc(collection(db, 'loginLogs'), {
          userId: newLog.userId,
          email: newLog.email,
          userName: newLog.userName,
          role: newLog.role,
          timestamp: newLog.timestamp,
          createdAt: serverTimestamp(),
          status: newLog.status,
          userAgent: newLog.userAgent,
        });
        if (user && status === 'Success') {
          await updateDoc(doc(db, 'users', user.id), {
            lastLogin: timestamp,
          });
        }
      } catch (e) {
        console.error('Failed to write login log to Firestore:', e);
      }
    } else {
      const existingLogs: LoginLog[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.LOGS) || '[]');
      const updatedLogs = [newLog, ...existingLogs].slice(0, 100);
      localStorage.setItem(STORAGE_KEYS.LOGS, JSON.stringify(updatedLogs));
      setLoginLogs(updatedLogs);
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: SDSUser }> => {
    const cleanEmail = email.trim().toLowerCase();

    if (isFirebaseConfigured && auth && db) {
      try {
        const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
        const userDocRef = doc(db, 'users', cred.user.uid);
        const userDoc = await getDoc(userDocRef);

        let loggedInUser: SDSUser;
        if (userDoc.exists()) {
          loggedInUser = { id: cred.user.uid, ...(userDoc.data() as Omit<SDSUser, 'id'>) };
          if (!loggedInUser.active) {
            await firebaseSignOut(auth);
            await recordLoginEvent(loggedInUser, cleanEmail, 'Failed');
            return { success: false, error: 'This driver account has been deactivated by an administrator.' };
          }
        } else {
          const defaultRole: UserRole = cleanEmail.includes('admin') ? 'admin' : 'driver';
          loggedInUser = {
            id: cred.user.uid,
            email: cleanEmail,
            name: cred.user.displayName || cleanEmail.split('@')[0],
            phone: '',
            role: defaultRole,
            status: 'Available',
            active: true,
            knowledgeTrainingRequired: defaultRole === 'driver',
            knowledgeTopicsRead: [],
            lastLogin: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          };
          await setDoc(userDocRef, loggedInUser);
        }

        await recordLoginEvent(loggedInUser, cleanEmail, 'Success');
        setCurrentUser(loggedInUser);
        void fetchFirestoreData(loggedInUser);
        return { success: true, user: loggedInUser };
      } catch (err: any) {
        await recordLoginEvent(null, cleanEmail, 'Failed');
        return {
          success: false,
          error: err?.message || 'Invalid email or password.',
        };
      }
    } else {
      // Local / Demo Firebase Emulator mode
      const savedUsers: SDSUser[] = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.USERS) || JSON.stringify(INITIAL_DEMO_USERS)
      );
      const passwords: Record<string, string> = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.PASSWORDS) || '{}'
      );

      const foundUser = savedUsers.find((u) => u.email.toLowerCase() === cleanEmail);
      const expectedPassword = passwords[cleanEmail];

      if (!foundUser || (expectedPassword && expectedPassword !== password)) {
        await recordLoginEvent(foundUser || null, cleanEmail, 'Failed');
        return { success: false, error: 'Invalid email or password.' };
      }

      if (!foundUser.active) {
        await recordLoginEvent(foundUser, cleanEmail, 'Failed');
        return { success: false, error: 'This driver login has been removed/deactivated.' };
      }

      const updatedUser: SDSUser = {
        ...foundUser,
        lastLogin: new Date().toISOString(),
      };

      const updatedUsers = savedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u));
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(updatedUser));
      setUsers(updatedUsers);
      setCurrentUser(updatedUser);
      await recordLoginEvent(updatedUser, cleanEmail, 'Success');

      return { success: true, user: updatedUser };
    }
  };

  const logout = async () => {
    if (isFirebaseConfigured && auth) {
      await firebaseSignOut(auth);
    }
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    setCurrentUser(null);
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      return { success: false, error: 'Please enter a valid email address.' };
    }

    if (isFirebaseConfigured && auth) {
      try {
        const actionCodeSettings = {
          url: `${window.location.origin}/login`,
          handleCodeInApp: false,
        };
        await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
        return { success: true };
      } catch (err: any) {
        // Fallback if the current origin isn't in Firebase Authorized Domains yet
        if (err?.code === 'auth/unauthorized-continue-uri') {
          try {
            await sendPasswordResetEmail(auth, cleanEmail);
            return { success: true };
          } catch (retryErr: any) {
            return {
              success: false,
              error: retryErr?.message || 'Failed to send password reset email.',
            };
          }
        }
        return {
          success: false,
          error: err?.message || 'Failed to send password reset email.',
        };
      }
    } else {
      const found = users.some((u) => u.email.toLowerCase() === cleanEmail);
      if (!found) {
        return { success: false, error: 'No account found with that email address.' };
      }
      return { success: true };
    }
  };

  const addDriverAccount = async (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    role: UserRole;
    status: DriverStatus;
  }): Promise<{ success: boolean; error?: string }> => {
    const cleanEmail = data.email.trim().toLowerCase();

    if (isFirebaseConfigured && db) {
      // Use a secondary Firebase App instance so creating a driver doesn't sign out the current Admin
      const secondaryAppName = `SecondaryApp-${Date.now()}`;
      const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);
      try {
        const userCred = await createUserWithEmailAndPassword(
          secondaryAuth,
          cleanEmail,
          data.password
        );
        const newUser: SDSUser = {
          id: userCred.user.uid,
          email: cleanEmail,
          name: data.name.trim(),
          phone: data.phone.trim(),
          role: data.role,
          status: data.status,
          active: true,
          createdAt: new Date().toISOString(),
          ...(data.role === 'driver'
            ? { knowledgeTrainingRequired: true, knowledgeTopicsRead: [] as string[] }
            : {}),
        };

        await setDoc(doc(db, 'users', newUser.id), newUser);

        const initialSched: WeeklySchedule = {
          driverId: newUser.id,
          driverName: newUser.name,
          shifts: {
            Monday: 'Off',
            Tuesday: 'Off',
            Wednesday: 'Off',
            Thursday: 'Off',
            Friday: 'Off',
            Saturday: 'Off',
            Sunday: 'Off',
          },
          updatedAt: new Date().toISOString(),
        };
        await setDoc(doc(db, 'schedules', newUser.id), initialSched);

        await firebaseSignOut(secondaryAuth);
        await deleteApp(secondaryApp);
        await fetchFirestoreData();
        return { success: true };
      } catch (err: any) {
        await deleteApp(secondaryApp).catch(() => {});
        return {
          success: false,
          error: err?.message || 'Failed to create Firebase login.',
        };
      }
    } else {
      // Local mode
      if (users.some((u) => u.email.toLowerCase() === cleanEmail)) {
        return { success: false, error: 'A user with this email already exists.' };
      }

      const newUser: SDSUser = {
        id: `user-${Date.now()}`,
        email: cleanEmail,
        name: data.name.trim(),
        phone: data.phone.trim(),
        role: data.role,
        status: data.status,
        active: true,
        createdAt: new Date().toISOString(),
        ...(data.role === 'driver'
          ? { knowledgeTrainingRequired: true, knowledgeTopicsRead: [] as string[] }
          : {}),
      };

      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));

      const passwords: Record<string, string> = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.PASSWORDS) || '{}'
      );
      passwords[cleanEmail] = data.password;
      localStorage.setItem(STORAGE_KEYS.PASSWORDS, JSON.stringify(passwords));

      const newSched: WeeklySchedule = {
        driverId: newUser.id,
        driverName: newUser.name,
        shifts: {
          Monday: 'Off',
          Tuesday: 'Off',
          Wednesday: 'Off',
          Thursday: 'Off',
          Friday: 'Off',
          Saturday: 'Off',
          Sunday: 'Off',
        },
        updatedAt: new Date().toISOString(),
      };
      const updatedSchedules = [...schedules, newSched];
      setSchedules(updatedSchedules);
      localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(updatedSchedules));

      return { success: true };
    }
  };

  const removeDriverAccount = async (userId: string) => {
    if (isFirebaseConfigured && db) {
      await deleteDoc(doc(db, 'users', userId));
      await deleteDoc(doc(db, 'schedules', userId)).catch(() => {});
      await fetchFirestoreData();
    } else {
      const target = users.find((u) => u.id === userId);
      const updatedUsers = users.filter((u) => u.id !== userId);
      const updatedSchedules = schedules.filter((s) => s.driverId !== userId);
      setUsers(updatedUsers);
      setSchedules(updatedSchedules);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
      localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(updatedSchedules));

      if (target) {
        const passwords: Record<string, string> = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.PASSWORDS) || '{}'
        );
        delete passwords[target.email.toLowerCase()];
        localStorage.setItem(STORAGE_KEYS.PASSWORDS, JSON.stringify(passwords));
      }
    }
  };

  const updateDriverStatus = async (userId: string, status: DriverStatus) => {
    if (isFirebaseConfigured && db) {
      await updateDoc(doc(db, 'users', userId), { status });
      await fetchFirestoreData();
    } else {
      const updatedUsers = users.map((u) => (u.id === userId ? { ...u, status } : u));
      setUsers(updatedUsers);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
    }
  };

  const updateUserRole = async (userId: string, role: UserRole) => {
    if (isFirebaseConfigured && db) {
      await updateDoc(doc(db, 'users', userId), { role });
      await fetchFirestoreData();
    } else {
      const updatedUsers = users.map((u) => (u.id === userId ? { ...u, role } : u));
      setUsers(updatedUsers);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
    }
  };

  const updateUserDetails = async (
    userId: string,
    updates: { name: string; email: string; phone?: string; role?: UserRole }
  ): Promise<{ success: boolean; error?: string }> => {
    const cleanName = updates.name.trim();
    const cleanEmail = updates.email.trim().toLowerCase();
    if (!cleanName || !cleanEmail) {
      return { success: false, error: 'Name and Email cannot be empty.' };
    }

    if (isFirebaseConfigured && db) {
      try {
        const payload: Partial<SDSUser> = {
          name: cleanName,
          email: cleanEmail,
        };
        if (updates.phone !== undefined) payload.phone = updates.phone.trim();
        if (updates.role !== undefined) payload.role = updates.role;

        await updateDoc(doc(db, 'users', userId), payload);

        // Also keep driverName synced in schedules if present
        const existingSched = schedules.find((s) => s.driverId === userId);
        if (existingSched) {
          await updateDoc(doc(db, 'schedules', userId), { driverName: cleanName }).catch(() => {});
        }

        if (currentUser?.id === userId) {
          setCurrentUser((prev) => (prev ? { ...prev, ...payload } : prev));
        }

        await fetchFirestoreData();
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to update user details in Firebase.',
        };
      }
    } else {
      const existingUser = users.find((u) => u.id === userId);
      const updatedUsers = users.map((u) =>
        u.id === userId
          ? {
              ...u,
              name: cleanName,
              email: cleanEmail,
              phone: updates.phone !== undefined ? updates.phone.trim() : u.phone,
              role: updates.role !== undefined ? updates.role : u.role,
            }
          : u
      );
      setUsers(updatedUsers);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));

      if (existingUser && existingUser.email.toLowerCase() !== cleanEmail) {
        const passwords: Record<string, string> = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.PASSWORDS) || '{}'
        );
        const oldEmailKey = existingUser.email.toLowerCase();
        if (passwords[oldEmailKey]) {
          passwords[cleanEmail] = passwords[oldEmailKey];
          delete passwords[oldEmailKey];
          localStorage.setItem(STORAGE_KEYS.PASSWORDS, JSON.stringify(passwords));
        }
      }

      const updatedSchedules = schedules.map((s) =>
        s.driverId === userId ? { ...s, driverName: cleanName } : s
      );
      setSchedules(updatedSchedules);
      localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(updatedSchedules));

      if (currentUser?.id === userId) {
        const updatedCurrent = updatedUsers.find((u) => u.id === userId) || null;
        setCurrentUser(updatedCurrent);
        if (updatedCurrent) {
          localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(updatedCurrent));
        }
      }

      return { success: true };
    }
  };

  const requireKnowledgeQuizRetake = async (
    userId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only an administrator can require a quiz retake.' };
    }
    const target = users.find((user) => user.id === userId);
    if (!target || target.role !== 'driver') {
      return { success: false, error: 'Quiz retakes can only be required for drivers.' };
    }

    const clearScore = (user: SDSUser): SDSUser => {
      const next: SDSUser = {
        ...user,
        knowledgeTrainingRequired: true,
        knowledgeQuizPassed: false,
      };
      delete next.knowledgeQuizScore;
      return next;
    };

    if (isFirebaseConfigured && db) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          knowledgeTrainingRequired: true,
          knowledgeQuizPassed: false,
          knowledgeQuizScore: deleteField(),
        });
        setUsers((prev) => prev.map((user) => (user.id === userId ? clearScore(user) : user)));
        return { success: true };
      } catch (err: any) {
        return { success: false, error: err?.message || 'Could not require a quiz retake.' };
      }
    }

    const updatedUsers = users.map((user) => (user.id === userId ? clearScore(user) : user));
    setUsers(updatedUsers);
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
    if (currentUser?.id === userId) {
      const updatedCurrent = updatedUsers.find((user) => user.id === userId) || null;
      setCurrentUser(updatedCurrent);
      if (updatedCurrent) {
        localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(updatedCurrent));
      }
    }
    return { success: true };
  };

  const updateDriverSchedule = async (
    driverId: string,
    driverName: string,
    shifts: Record<DayOfWeek, string>,
    markedOff?: Record<string, boolean>,
    logMeta?: {
      day: string;
      windowLabel: string;
      action: ScheduleLog['action'];
      details: string;
    },
    dateShifts?: Record<string, string>
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return {
        success: false,
        error: 'Only Administrators are permitted to modify driver schedules.',
      };
    }

    const allDays: DayOfWeek[] = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    const structuredWindows = allDays.reduce((acc, day) => {
      acc[day] = parseShiftWindows(shifts[day]);
      return acc;
    }, {} as Record<DayOfWeek, DeliveryWindowKey[]>);

    const existingSched = schedules.find((s) => s.driverId === driverId);
    const timestamp = new Date().toISOString();
    const updatedSched: WeeklySchedule = {
      driverId,
      driverName,
      shifts,
      windows: structuredWindows,
      dateShifts: dateShifts !== undefined ? dateShifts : existingSched?.dateShifts || {},
      markedOff: markedOff !== undefined ? markedOff : existingSched?.markedOff || {},
      updatedAt: timestamp,
    };

    const newLogEntry: Omit<ScheduleLog, 'id'> | null = logMeta
      ? {
          adminId: currentUser.id,
          adminName: currentUser.name,
          adminEmail: currentUser.email,
          driverId,
          driverName,
          day: logMeta.day,
          windowLabel: logMeta.windowLabel,
          action: logMeta.action,
          details: logMeta.details,
          timestamp,
        }
      : null;

    if (isFirebaseConfigured && db) {
      try {
        await setDoc(doc(db, 'schedules', driverId), updatedSched, { merge: true });
        if (newLogEntry) {
          await addDoc(collection(db, 'scheduleLogs'), {
            ...newLogEntry,
            createdAt: serverTimestamp(),
          });
        }
        setSchedules((prev) => {
          const exists = prev.some((s) => s.driverId === driverId);
          return exists
            ? prev.map((s) => (s.driverId === driverId ? updatedSched : s))
            : [...prev, updatedSched];
        });
        return { success: true };
      } catch (err: any) {
        console.error('Error saving schedule to Firebase:', err);
        return {
          success: false,
          error: err?.message || 'Failed to save schedule to Firebase Firestore.',
        };
      }
    } else {
      const exists = schedules.some((s) => s.driverId === driverId);
      const updatedSchedules = exists
        ? schedules.map((s) => (s.driverId === driverId ? updatedSched : s))
        : [...schedules, updatedSched];
      setSchedules(updatedSchedules);
      localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(updatedSchedules));

      if (newLogEntry) {
        const localLog: ScheduleLog = { id: `sched-log-${Date.now()}`, ...newLogEntry };
        const existingLogs: ScheduleLog[] = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.SCHEDULE_LOGS) || '[]'
        );
        const updatedLogs = [localLog, ...existingLogs].slice(0, 100);
        localStorage.setItem(STORAGE_KEYS.SCHEDULE_LOGS, JSON.stringify(updatedLogs));
        setScheduleLogs(updatedLogs);
      }

      return { success: true };
    }
  };

  const clearAllDriverSchedules = async (): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return {
        success: false,
        error: 'Only Administrators are permitted to clear driver schedules.',
      };
    }

    const offShifts: Record<DayOfWeek, string> = {
      Monday: 'Off',
      Tuesday: 'Off',
      Wednesday: 'Off',
      Thursday: 'Off',
      Friday: 'Off',
      Saturday: 'Off',
      Sunday: 'Off',
    };
    const offWindows: Record<DayOfWeek, DeliveryWindowKey[]> = {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    };
    const timestamp = new Date().toISOString();
    const names = new Map<string, string>();
    users.forEach((user) => names.set(user.id, user.name));
    schedules.forEach((sched) => {
      if (!names.has(sched.driverId)) names.set(sched.driverId, sched.driverName);
    });

    const cleared: WeeklySchedule[] = [...names.entries()].map(([driverId, driverName]) => ({
      driverId,
      driverName,
      shifts: { ...offShifts },
      windows: { ...offWindows },
      dateShifts: {},
      markedOff: {},
      updatedAt: timestamp,
    }));

    const logEntry: Omit<ScheduleLog, 'id'> = {
      adminId: currentUser.id,
      adminName: currentUser.name,
      adminEmail: currentUser.email,
      driverId: 'all',
      driverName: 'All drivers',
      day: 'All days',
      windowLabel: 'All windows',
      action: 'Removed',
      details: 'Cleared the entire schedule for every driver.',
      timestamp,
    };

    if (isFirebaseConfigured && db) {
      try {
        const batch = writeBatch(db);
        cleared.forEach((sched) => {
          batch.set(doc(db, 'schedules', sched.driverId), sched);
        });
        const logRef = doc(collection(db, 'scheduleLogs'));
        batch.set(logRef, { ...logEntry, createdAt: serverTimestamp() });
        await batch.commit();
        setSchedules(cleared);
        setScheduleLogs((prev) => [{ id: logRef.id, ...logEntry }, ...prev].slice(0, 100));
        return { success: true };
      } catch (err: any) {
        console.error('Error clearing schedules in Firebase:', err);
        return {
          success: false,
          error: err?.message || 'Failed to clear schedules in Firebase.',
        };
      }
    }

    setSchedules(cleared);
    localStorage.setItem(STORAGE_KEYS.SCHEDULES, JSON.stringify(cleared));
    const localLog: ScheduleLog = { id: `sched-log-${Date.now()}`, ...logEntry };
    const existingLogs: ScheduleLog[] = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SCHEDULE_LOGS) || '[]'
    );
    const updatedLogs = [localLog, ...existingLogs].slice(0, 100);
    localStorage.setItem(STORAGE_KEYS.SCHEDULE_LOGS, JSON.stringify(updatedLogs));
    setScheduleLogs(updatedLogs);
    return { success: true };
  };

  const saveKnowledgeArticle = async (
    article: Omit<KnowledgeBaseArticle, 'id' | 'updatedAt' | 'updatedBy'> & { id?: string }
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only Administrators can add or edit Knowledge Base guides.' };
    }

    const articleId = article.id || `kb-${Date.now()}`;
    const existing = knowledgeBase.find((item) => item.id === articleId);
    const categoryChanged = existing && existing.category !== article.category;
    const siblingOrders = knowledgeBase
      .filter((item) => item.category === article.category && item.id !== articleId)
      .map((item) => item.order ?? -1);
    const nextOrder =
      existing && !categoryChanged && existing.order !== undefined
        ? existing.order
        : Math.max(-1, ...siblingOrders) + 1;
    const fullArticle: KnowledgeBaseArticle = {
      id: articleId,
      category: article.category,
      title: article.title.trim(),
      summary: article.summary.trim(),
      steps: article.steps.map((s) => s.trim()).filter(Boolean),
      notes: article.notes?.trim() || '',
      order: nextOrder,
      updatedAt: new Date().toISOString(),
      updatedBy: currentUser.name,
    };

    if (isFirebaseConfigured && db) {
      try {
        await setDoc(doc(db, 'knowledgeBase', articleId), fullArticle, { merge: true });
        setKnowledgeBase((prev) => {
          const exists = prev.some((a) => a.id === articleId);
          return exists
            ? prev.map((a) => (a.id === articleId ? fullArticle : a))
            : [...prev, fullArticle];
        });
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to save Knowledge Base article to Firebase.',
        };
      }
    } else {
      const exists = knowledgeBase.some((a) => a.id === articleId);
      const updated = exists
        ? knowledgeBase.map((a) => (a.id === articleId ? fullArticle : a))
        : [...knowledgeBase, fullArticle];
      setKnowledgeBase(updated);
      localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_BASE, JSON.stringify(updated));
      return { success: true };
    }
  };

  const deleteKnowledgeArticle = async (
    articleId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only Administrators can delete Knowledge Base guides.' };
    }

    if (isFirebaseConfigured && db) {
      try {
        await deleteDoc(doc(db, 'knowledgeBase', articleId));
        setKnowledgeBase((prev) => prev.filter((a) => a.id !== articleId));
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to delete Knowledge Base article.',
        };
      }
    } else {
      const updated = knowledgeBase.filter((a) => a.id !== articleId);
      setKnowledgeBase(updated);
      localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_BASE, JSON.stringify(updated));
      return { success: true };
    }
  };

  const reorderKnowledgeTopics = async (
    category: KBCategory,
    orderedIds: string[]
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only Administrators can reorder Knowledge Base topics.' };
    }

    const inCategory = topicsInCategory(knowledgeBase, category);
    const byId = new Map(inCategory.map((item) => [item.id, item]));
    const uniqueIds = orderedIds.filter(
      (id, index) => byId.has(id) && orderedIds.indexOf(id) === index
    );
    const missingIds = inCategory
      .map((item) => item.id)
      .filter((id) => !uniqueIds.includes(id));
    const finalIds = [...uniqueIds, ...missingIds];
    if (finalIds.length === 0) {
      return { success: true };
    }

    const withOrder = finalIds.map((id, index) => ({ ...byId.get(id)!, order: index }));
    const orderById = new Map(withOrder.map((item) => [item.id, item.order]));
    const updatedAll = knowledgeBase.map((item) =>
      orderById.has(item.id) ? { ...item, order: orderById.get(item.id) } : item
    );

    if (isFirebaseConfigured && db) {
      try {
        const batch = writeBatch(db);
        withOrder.forEach((item) => {
          batch.set(doc(db, 'knowledgeBase', item.id), { order: item.order }, { merge: true });
        });
        await batch.commit();
        setKnowledgeBase(updatedAll);
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to save the new topic order.',
        };
      }
    }

    setKnowledgeBase(updatedAll);
    localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_BASE, JSON.stringify(updatedAll));
    return { success: true };
  };

  const updateKnowledgeSection = async (
    previousKey: string,
    updates: { key: string; title: string; subtitle: string }
  ): Promise<{ success: boolean; error?: string }> => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only Administrators can edit Knowledge Base sections.' };
    }

    const nextKey = updates.key.trim();
    const nextTitle = updates.title.trim();
    const nextSubtitle = updates.subtitle.trim();
    if (!nextKey || !nextTitle) {
      return { success: false, error: 'Section heading and category are required.' };
    }

    const current = kbSections.find((section) => section.key === previousKey);
    if (!current) {
      return { success: false, error: 'That section could not be found.' };
    }
    if (
      nextKey !== previousKey &&
      kbSections.some((section) => section.key === nextKey)
    ) {
      return { success: false, error: 'Another section already uses that category name.' };
    }

    const nextSections = kbSections.map((section) =>
      section.key === previousKey
        ? { ...section, key: nextKey, title: nextTitle, subtitle: nextSubtitle }
        : section
    );
    const movedArticles =
      nextKey === previousKey
        ? knowledgeBase
        : knowledgeBase.map((article) =>
            article.category === previousKey ? { ...article, category: nextKey } : article
          );

    if (isFirebaseConfigured && db) {
      const database = db;
      try {
        const batch = writeBatch(database);
        batch.set(
          doc(database, 'knowledgeBase', KB_SECTIONS_DOC_ID),
          { kind: 'sections', sections: nextSections },
          { merge: true }
        );
        if (nextKey !== previousKey) {
          knowledgeBase
            .filter((article) => article.category === previousKey)
            .forEach((article) => {
              batch.set(
                doc(database, 'knowledgeBase', article.id),
                { category: nextKey },
                { merge: true }
              );
            });
        }
        await batch.commit();
        setKbSections(nextSections);
        if (nextKey !== previousKey) setKnowledgeBase(movedArticles);
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to update the Knowledge Base section.',
        };
      }
    }

    setKbSections(nextSections);
    localStorage.setItem(STORAGE_KEYS.KB_SECTIONS, JSON.stringify(nextSections));
    if (nextKey !== previousKey) {
      setKnowledgeBase(movedArticles);
      localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_BASE, JSON.stringify(movedArticles));
    }
    return { success: true };
  };

  const saveUserTrainingPatch = async (updates: Partial<SDSUser>) => {
    if (!currentUser) return { success: false, error: 'You need to be signed in.' };
    const nextUser = { ...currentUser, ...updates };
    setCurrentUser(nextUser);
    setUsers((prev) => prev.map((user) => (user.id === currentUser.id ? nextUser : user)));
    try {
      if (isFirebaseConfigured && db) {
        await updateDoc(doc(db, 'users', currentUser.id), updates);
      } else {
        const saved = localStorage.getItem(STORAGE_KEYS.USERS);
        const parsed: SDSUser[] = saved ? JSON.parse(saved) : users;
        const nextUsers = parsed.map((user) => (user.id === currentUser.id ? nextUser : user));
        localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(nextUsers));
        localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(nextUser));
      }
      return { success: true };
    } catch (err) {
      setCurrentUser(currentUser);
      setUsers((prev) => prev.map((user) => (user.id === currentUser.id ? currentUser : user)));
      throw err;
    }
  };

  const persistQuizBank = async (questions: KnowledgeQuizQuestion[]) => {
    if (currentUser?.role !== 'admin') {
      return { success: false, error: 'Only an administrator can edit quiz questions.' };
    }
    const clean = normalizeStoredQuizQuestions(questions);
    try {
      if (isFirebaseConfigured && db) {
        await setDoc(doc(db, 'knowledgeBase', QUIZ_BANK_DOC_ID), {
          kind: 'quiz-bank',
          questions: clean,
        });
      } else {
        localStorage.setItem(STORAGE_KEYS.QUIZ_BANK, JSON.stringify(clean));
      }
      setQuizQuestions(clean);
      setQuizBankReady(true);
      return { success: true, count: clean.length };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Could not save the quiz questions.' };
    }
  };

  const saveQuizQuestion = async (question: KnowledgeQuizQuestion) => {
    const next = quizQuestions.some((item) => item.id === question.id)
      ? quizQuestions.map((item) => (item.id === question.id ? question : item))
      : [...quizQuestions, question];
    return persistQuizBank(next);
  };

  const deleteQuizQuestion = async (questionId: string) => {
    return persistQuizBank(quizQuestions.filter((item) => item.id !== questionId));
  };

  const publishGeneratedQuiz = async () => {
    const generated = buildKnowledgeQuiz(knowledgeBase);
    if (generated.length === 0) {
      return { success: false, error: 'The knowledge base does not have enough material for a quiz yet.' };
    }
    return persistQuizBank(generated);
  };

  const markKnowledgeTopicRead = async (
    articleId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!driverNeedsKnowledgeTraining(currentUser)) {
      return { success: false, error: 'Knowledge training is not required for this account.' };
    }
    const already = currentUser?.knowledgeTopicsRead || [];
    if (already.includes(articleId)) return { success: true };
    try {
      await saveUserTrainingPatch({ knowledgeTopicsRead: [...already, articleId] });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Could not save your reading progress.' };
    }
  };

  const submitKnowledgeQuiz = async (
    correctCount: number,
    questionCount: number
  ): Promise<{ success: boolean; error?: string; passed?: boolean; score?: number }> => {
    if (!currentUser || !driverNeedsKnowledgeTraining(currentUser)) {
      return { success: false, error: 'This quiz is only for new drivers who still need to pass.' };
    }
    if (questionCount < 1) {
      return { success: false, error: 'The quiz does not have any questions yet.' };
    }
    const score = Math.round((correctCount / questionCount) * 100);
    const passed = score >= KNOWLEDGE_QUIZ_PASS_PERCENT;
    const completedAt = new Date().toISOString();
    const attempts = (currentUser.knowledgeQuizAttempts || 0) + 1;
    try {
      await saveUserTrainingPatch({
        knowledgeQuizScore: score,
        knowledgeQuizPassed: passed,
        knowledgeQuizCompletedAt: completedAt,
        knowledgeQuizAttempts: attempts,
      });
    } catch (err: any) {
      return { success: false, error: err?.message || 'Could not save your quiz result.' };
    }

    const scoreRecord: Omit<TrainingScore, 'id'> = {
      driverId: currentUser.id,
      driverName: currentUser.name,
      driverEmail: currentUser.email,
      moduleName: 'Knowledge Base Quiz',
      score,
      maxScore: 100,
      passed,
      completedAt,
    };
    try {
      if (isFirebaseConfigured && db) {
        const database = db;
        const scoreRef = await addDoc(collection(database, 'trainingScores'), scoreRecord);
        setTrainingScores((prev) => [{ id: scoreRef.id, ...scoreRecord }, ...prev]);
      } else {
        const localScore: TrainingScore = { id: `score-${Date.now()}`, ...scoreRecord };
        const nextScores = [localScore, ...trainingScores];
        setTrainingScores(nextScores);
        localStorage.setItem(STORAGE_KEYS.SCORES, JSON.stringify(nextScores));
      }
    } catch {
      // The driver's score is already saved on their profile.
    }
    return { success: true, passed, score };
  };

  const seedAdminAccount = async (
    email: string,
    password: string,
    name: string = 'SDS Administrator'
  ): Promise<{ success: boolean; error?: string }> => {
    const cleanEmail = email.trim().toLowerCase();

    if (isFirebaseConfigured && auth && db) {
      try {
        let uid: string;
        try {
          const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          uid = cred.user.uid;
        } catch (createErr: any) {
          if (createErr?.code === 'auth/email-already-in-use') {
            const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
            uid = cred.user.uid;
          } else {
            throw createErr;
          }
        }

        const adminProfile: SDSUser = {
          id: uid,
          email: cleanEmail,
          name: name.trim() || 'SDS Administrator',
          phone: '(602) 555-0100',
          role: 'admin',
          status: 'Available',
          active: true,
          lastLogin: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        };

        await setDoc(doc(db, 'users', uid), adminProfile);
        await recordLoginEvent(adminProfile, cleanEmail, 'Success');
        setCurrentUser(adminProfile);
        await fetchFirestoreData();
        return { success: true };
      } catch (err: any) {
        return {
          success: false,
          error: err?.message || 'Failed to seed admin account in Firebase.',
        };
      }
    } else {
      const res = await addDriverAccount({
        name: name.trim() || 'SDS Administrator',
        email: cleanEmail,
        phone: '(602) 555-0100',
        password,
        role: 'admin',
        status: 'Available',
      });
      if (!res.success && !res.error?.includes('already exists')) {
        return res;
      }
      const loginRes = await login(cleanEmail, password);
      return { success: loginRes.success, error: loginRes.error };
    }
  };

  const refreshData = async () => {
    if (isFirebaseConfigured && db) {
      await fetchFirestoreData();
    } else {
      loadLocalData();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        isFirebaseLive: isFirebaseConfigured,
        users,
        schedules,
        trainingScores,
        loginLogs,
        scheduleLogs,
        knowledgeBase,
        kbSections,
        quizQuestions,
        quizBankReady,
        login,
        logout,
        resetPassword,
        addDriverAccount,
        removeDriverAccount,
        updateDriverStatus,
        updateUserRole,
        updateUserDetails,
        requireKnowledgeQuizRetake,
        updateDriverSchedule,
        clearAllDriverSchedules,
        seedAdminAccount,
        saveKnowledgeArticle,
        deleteKnowledgeArticle,
        reorderKnowledgeTopics,
        updateKnowledgeSection,
        markKnowledgeTopicRead,
        saveQuizQuestion,
        deleteQuizQuestion,
        publishGeneratedQuiz,
        submitKnowledgeQuiz,
        quizInProgress,
        setKnowledgeQuizActive,
        refreshData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
